import { z } from "zod";
import { ClaimableRoleSchema, ParticipantTypeSchema, RoleSchema, } from "./common.js";
/**
 * A reference to anyone (or anything) that can appear in a group's expenses.
 * `id` is the stable server-assigned UUID expense `payments` and item splits
 * reference; it survives a guest → user claim.
 */
export const ParticipantSchema = z.strictObject({
    id: z.string().uuid(),
    type: ParticipantTypeSchema,
    /** Set only on `USER` participants. */
    userId: z.string().uuid().optional(),
    displayName: z.string().min(1).max(64),
    role: RoleSchema,
    /** Set only on participants who started as `GUEST` and were later claimed. */
    claimedAt: z.string().datetime().optional(),
});
export const AddParticipantRequestSchema = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("GUEST"),
        displayName: z.string().min(1).max(64),
        role: RoleSchema.default("MEMBER"),
    }),
    z.strictObject({
        type: z.literal("USER"),
        userId: z.string().uuid(),
        role: RoleSchema.default("MEMBER"),
    }),
]);
export const UpdateParticipantRequestSchema = z.strictObject({
    displayName: z.string().min(1).max(64).optional(),
    role: RoleSchema.optional(),
});
/**
 * Body of `POST /groups/{groupId}/participants/{participantId}/transfer`.
 *
 * Moves everything the participant in the path is involved in — expenses they
 * paid or share, and settlements they sent or received — onto
 * `targetParticipantId`, then deletes the emptied participant. The case it
 * exists for: someone was added as a `GUEST`, later joined with their own
 * account as a separate participant, and now has two identities in one group
 * (issue #448).
 */
export const TransferParticipantRequestSchema = z.strictObject({
    targetParticipantId: z.string().uuid(),
    /**
     * Keep the emptied participant instead of deleting it. Defaults to deleting,
     * which is the point of the operation; set `false` to reassign history while
     * leaving the row in place.
     */
    removeSource: z.boolean().optional(),
});
/**
 * What a transfer moved. Reported because the operation is a sequence of
 * per-row writes rather than one transaction, so a caller (and an operator
 * reading logs) can see exactly how far it got.
 */
export const TransferParticipantResponseSchema = z.strictObject({
    /** Expenses whose payments or splits were repointed. */
    expensesUpdated: z.number().int().nonnegative(),
    /** Settlements repointed to the target. */
    settlementsUpdated: z.number().int().nonnegative(),
    /**
     * Settlements deleted because they were *between* the two participants: once
     * the two are one person, a payment from someone to themselves is invalid and
     * means nothing. Deleting one leaves the merged participant's net unchanged.
     */
    settlementsDeleted: z.number().int().nonnegative(),
    /** Whether the emptied participant was deleted. */
    sourceRemoved: z.boolean(),
});
/** Roles a join request can propose / be approved with. */
export { ClaimableRoleSchema };
